import React, { useState, useEffect } from 'react';
import { useFormContext, Controller, get, useWatch, Path } from 'react-hook-form';
import { Search, Paperclip, X, Trash2 } from 'lucide-react';

import { OrderSchemaType, ItemPath } from '../../../order.schema';
import { useApiData } from '../../../contexts/ApiDataContext';
import { useAttachmentHandler } from '../../../Hooks/useAttachmentHandler';
import SteelOrdersInfiniteSelect from '../../../components/SteelOrdersInfiniteSelect';
import { NumericInput } from '../../../components/NumericInput';
import axios from '../../../../../../../config/axios';

import {
  FormGrid,
  FormGroup,
  Input,
  Label,
  Select,
  AttachmentList,
  AttachmentListItem,
  RemoveAttachmentButton,
  FormErrorMessage,
  AttachmentInputContainer,
  AttachmentTrigger,
  AttachmentPlaceholder,
  TrashIconContainer,
} from '../../../style';

interface ServicesSectionProps {
  itemPath: ItemPath;
}

const ServicesSection: React.FC<ServicesSectionProps> = ({ itemPath }) => {
  const { register, control, formState: { errors }, setValue, watch } = useFormContext<OrderSchemaType>();

  const [selectedServiceCategory, setSelectedServiceCategory] = useState<{ id: number; name: string }[]>([]);
  const [selectedServiceType, setSelectedServiceType] = useState<{ id: number; name: string }[]>([]);
  const [selectedService, setSelectedService] = useState<{ id: number; name: string }[]>([]);
  const [selectedSupplier, setSelectedSupplier] = useState<{ id: number; name: string }[]>([]);
  


  const API_BASE_URL = `${process.env.REACT_APP_GATEWAY_BASE_URL}/steel-orders`;

  const {
    attachments,
    attachmentError,
    fileInputRef,
    handleFileSelectClick,
    handleFileChange,
    handleRemoveAttachment,
    handleRemoveAllAttachments,
  } = useAttachmentHandler({ fieldPath: `${itemPath}.stage.anexos` });

  const stagePath: `${ServicesSectionProps['itemPath']}.stage` = `${itemPath}.stage`;

	useEffect(() => {
		const existingStageId = watch((`${itemPath}.stageId`) as any) as number | undefined;
		if (selectedService.length > 0) {
			setValue(`${stagePath}.servico`, selectedService[0].name);
			setValue((`${itemPath}.stageId`) as any, selectedService[0].id, { shouldDirty: true, shouldValidate: false });
			setSelectedServiceCategory([]);
			setSelectedServiceType([]);
      setSelectedSupplier([]);
      setValue((`${itemPath}.serviceCategoryId`) as any, undefined, { shouldDirty: true, shouldValidate: false });
      setValue((`${itemPath}.serviceTypeId`) as any, undefined, { shouldDirty: true, shouldValidate: false });
      setValue((`${stagePath}.categoria`) as any, '', { shouldDirty: true, shouldValidate: false });
      setValue((`${stagePath}.tipo`) as any, '', { shouldDirty: true, shouldValidate: false });
      setValue((`${itemPath}.serviceSupplierCompanyId`) as any, undefined, { shouldDirty: true, shouldValidate: false });
      setValue((`${itemPath}.stage.fornecedor`) as any, '', { shouldDirty: true, shouldValidate: false });
		} else {
			setValue(`${stagePath}.servico`, '');
			if (existingStageId === undefined || existingStageId === null) {
				setValue((`${itemPath}.stageId`) as any, undefined, { shouldDirty: true, shouldValidate: false });
			}
			setSelectedServiceCategory([]);
			setSelectedServiceType([]);
      setSelectedSupplier([]);
      setValue((`${itemPath}.serviceCategoryId`) as any, undefined, { shouldDirty: true, shouldValidate: false });
      setValue((`${itemPath}.serviceTypeId`) as any, undefined, { shouldDirty: true, shouldValidate: false });
      setValue((`${stagePath}.categoria`) as any, '', { shouldDirty: true, shouldValidate: false });
      setValue((`${stagePath}.tipo`) as any, '', { shouldDirty: true, shouldValidate: false });
      setValue((`${itemPath}.serviceSupplierCompanyId`) as any, undefined, { shouldDirty: true, shouldValidate: false });
      setValue((`${itemPath}.stage.fornecedor`) as any, '', { shouldDirty: true, shouldValidate: false });
		}
	}, [selectedService, setValue, stagePath, itemPath, watch]);

  useEffect(() => {
    if (selectedServiceCategory.length > 0) {
      setValue(`${stagePath}.categoria`, selectedServiceCategory[0].name);
      setValue((`${itemPath}.serviceCategoryId`) as any, selectedServiceCategory[0].id, { shouldDirty: true, shouldValidate: false });
    }
  }, [selectedServiceCategory, setValue, stagePath]);

  useEffect(() => {
    if (selectedServiceType.length > 0) {
      setValue(`${stagePath}.tipo`, selectedServiceType[0].name);
      setValue((`${itemPath}.serviceTypeId`) as any, selectedServiceType[0].id, { shouldDirty: true, shouldValidate: false });
    }
  }, [selectedServiceType, setValue, stagePath]);

  useEffect(() => {
    if (selectedSupplier.length > 0) {
      setValue(`${itemPath}.stage.fornecedor`, selectedSupplier[0].name);
      setValue((`${itemPath}.serviceSupplierCompanyId`) as any, selectedSupplier[0].id, { shouldDirty: true, shouldValidate: false });
    }
  }, [selectedSupplier, setValue, itemPath]);

  useEffect(() => {
    const qty = Number(watch(`${itemPath}.stage.quantidade`));
    if (!isNaN(qty)) {
      setValue((`${itemPath}.quantity`) as any, qty, { shouldDirty: true, shouldValidate: false });
    }
  }, [watch(`${itemPath}.stage.quantidade`)]);

  useEffect(() => {
    const existingStageId = watch((`${itemPath}.stageId`) as any) as number | undefined;
    const scId = watch((`${itemPath}.serviceCategoryId`) as any) as number | undefined;
    const stId = watch((`${itemPath}.serviceTypeId`) as any) as number | undefined;
    const ssId = watch((`${itemPath}.serviceSupplierCompanyId`) as any) as number | undefined;
    if (existingStageId && selectedService.length === 0) {
      setSelectedService([{ id: existingStageId, name: watch(`${stagePath}.servico`) || '' }]);
    }
    if (scId && selectedServiceCategory.length === 0) {
      setSelectedServiceCategory([{ id: scId, name: watch(`${stagePath}.categoria`) || '' }]);
    }
    if (stId && selectedServiceType.length === 0) {
      setSelectedServiceType([{ id: stId, name: watch(`${stagePath}.tipo`) || '' }]);
    }
    if (ssId && selectedSupplier.length === 0) {
      setSelectedSupplier([{ id: ssId, name: watch(`${itemPath}.stage.fornecedor`) || '' }]);
    }
  }, [watch, itemPath, stagePath, selectedService.length, selectedServiceCategory.length, selectedServiceType.length, selectedSupplier.length]);

  const wStageId = useWatch({ control, name: (`${itemPath}.stageId` as any) });
  const wStageName = useWatch({ control, name: (`${stagePath}.servico` as any) });
  const wCatId = useWatch({ control, name: (`${itemPath}.serviceCategoryId` as any) });
  const wCatName = useWatch({ control, name: (`${stagePath}.categoria` as any) });
  const wTypeId = useWatch({ control, name: (`${itemPath}.serviceTypeId` as any) });
  const wTypeName = useWatch({ control, name: (`${stagePath}.tipo` as any) });
  const wSuppId = useWatch({ control, name: (`${itemPath}.serviceSupplierCompanyId` as any) });
  const wSuppName = useWatch({ control, name: (`${itemPath}.stage.fornecedor` as any) });

  useEffect(() => {
    if (wStageId && selectedService.length === 0) setSelectedService([{ id: Number(wStageId), name: String(wStageName || '') }]);
    if (wCatId && selectedServiceCategory.length === 0) setSelectedServiceCategory([{ id: Number(wCatId), name: String(wCatName || '') }]);
    if (wTypeId && selectedServiceType.length === 0) setSelectedServiceType([{ id: Number(wTypeId), name: String(wTypeName || '') }]);
    if (wSuppId && selectedSupplier.length === 0) setSelectedSupplier([{ id: Number(wSuppId), name: String(wSuppName || '') }]);
    
    const fetchMissingNames = async () => {
      try {
        if (wCatId && !wCatName) {
          const res = await fetch(`${API_BASE_URL}/select-in-order/service-categories`);
          const cats = await res.json();
          const cat = (Array.isArray(cats) ? cats : []).find((c: any) => Number(c.id) === Number(wCatId));
          if (cat?.name) {
            setValue((`${stagePath}.categoria`) as any, String(cat.name), { shouldDirty: false, shouldValidate: false });
            if (selectedServiceCategory.length === 0) setSelectedServiceCategory([{ id: Number(wCatId), name: String(cat.name) }]);
          }
        }
        if (wTypeId && !wTypeName) {
          const res = await fetch(`${API_BASE_URL}/select-in-order/service-types`);
          const types = await res.json();
          const tp = (Array.isArray(types) ? types : []).find((t: any) => Number(t.id) === Number(wTypeId));
          if (tp?.name) {
            setValue((`${stagePath}.tipo`) as any, String(tp.name), { shouldDirty: false, shouldValidate: false });
            if (selectedServiceType.length === 0) setSelectedServiceType([{ id: Number(wTypeId), name: String(tp.name) }]);
          }
        }
        if (wSuppId && !wSuppName) {
          let supplierName: string | undefined = undefined;
          if (wStageId) {
            const params = new URLSearchParams({ stageId: String(wStageId), limit: '100' });
            const res = await fetch(`${API_BASE_URL}/select-in-order/service-suppliers?${params.toString()}`);
            const data = await res.json();
            const list = Array.isArray(data?.suppliers) ? data.suppliers : Array.isArray(data) ? data : [];
            const found = list.find((s: any) => Number(s.id) === Number(wSuppId));
            supplierName = found?.name || found?.companyName;
          }
          if (!supplierName) {
            const resAll = await fetch(`${API_BASE_URL}/select-in-order/suppliers`);
            const dataAll = await resAll.json();
            const listAll = Array.isArray(dataAll?.suppliers) ? dataAll.suppliers : Array.isArray(dataAll) ? dataAll : [];
            const foundAll = listAll.find((s: any) => Number(s.id) === Number(wSuppId));
            supplierName = foundAll?.name || foundAll?.companyName;
          }
          if (supplierName) {
            setValue((`${itemPath}.stage.fornecedor`) as any, String(supplierName), { shouldDirty: false, shouldValidate: false });
            if (selectedSupplier.length === 0) setSelectedSupplier([{ id: Number(wSuppId), name: String(supplierName) }]);
          }
        }
      } catch (e) { void e; }
    };
    fetchMissingNames();
  }, [wStageId, wStageName, wCatId, wCatName, wTypeId, wTypeName, wSuppId, wSuppName, selectedService.length, selectedServiceCategory.length, selectedServiceType.length, selectedSupplier.length]);

  return (
    <>
      <FormGrid columns={3} style={{ marginTop: '16px' }}>
        <SteelOrdersInfiniteSelect
          label="Serviço"
          items={selectedService}
          setItems={setSelectedService}
          endpoint="services"
          placeholder="Procure por nome ou código"
          onCleared={() => {
            setValue((`${itemPath}.stageId`) as any, undefined, { shouldDirty: true, shouldValidate: false });
            setValue((`${stagePath}.servico`) as any, '', { shouldDirty: true, shouldValidate: false });
            setSelectedServiceCategory([]);
            setSelectedServiceType([]);
            setSelectedSupplier([]);
          }}
        />
        <SteelOrdersInfiniteSelect
          label="Categoria"
          items={selectedServiceCategory}
          setItems={setSelectedServiceCategory}
          endpoint="service-categories"
          placeholder="Selecione a categoria"
          additionalParams={selectedService[0]?.id ? { stageId: selectedService[0].id } : {}}
          searchPlaceholder={selectedService[0]?.id ? 'Nenhuma categoria encontrada' : 'Selecione um serviço primeiro'}
          isDisabled={!selectedService[0]?.id}
          key={selectedService[0]?.id ? `service-categories-${selectedService[0].id}` : 'service-categories-disabled'}
          onCleared={() => {
            setValue((`${itemPath}.serviceCategoryId`) as any, undefined, { shouldDirty: true, shouldValidate: false });
            setValue((`${stagePath}.categoria`) as any, '', { shouldDirty: true, shouldValidate: false });
          }}
        />
        <SteelOrdersInfiniteSelect
          label="Tipo"
          items={selectedServiceType}
          setItems={setSelectedServiceType}
          endpoint="service-types"
          placeholder="Selecione o tipo"
          additionalParams={selectedService[0]?.id && selectedServiceCategory[0]?.id ? { stageId: selectedService[0].id, categoryId: selectedServiceCategory[0].id } : {}}
          searchPlaceholder={selectedService[0]?.id ? (selectedServiceCategory[0]?.id ? 'Nenhum tipo encontrado' : 'Selecione uma categoria primeiro') : 'Selecione um serviço primeiro'}
          isDisabled={!selectedService[0]?.id || !selectedServiceCategory[0]?.id}
          key={selectedService[0]?.id && selectedServiceCategory[0]?.id ? `service-types-${selectedService[0].id}-${selectedServiceCategory[0].id}` : 'service-types-disabled'}
          onCleared={() => {
            setValue((`${itemPath}.serviceTypeId`) as any, undefined, { shouldDirty: true, shouldValidate: false });
            setValue((`${stagePath}.tipo`) as any, '', { shouldDirty: true, shouldValidate: false });
          }}
        />
      </FormGrid>

      <FormGrid columns={3} style={{ marginTop: '16px' }}>
        <SteelOrdersInfiniteSelect
          label="Fornecedor"
          items={selectedSupplier}
          setItems={setSelectedSupplier}
          endpoint="service-suppliers"
          placeholder="Procure por fornecedor do serviço"
          searchPlaceholder={selectedService[0]?.id ? 'Nenhum fornecedor encontrado' : 'Selecione um serviço primeiro'}
          additionalParams={selectedService[0]?.id ? { stageId: selectedService[0].id } : {}}
          key={selectedService[0]?.id || 'service-supplier'}
          isDisabled={!selectedService[0]?.id}
          onCleared={() => {
            setValue((`${itemPath}.serviceSupplierCompanyId`) as any, undefined, { shouldDirty: true, shouldValidate: false });
            setValue((`${itemPath}.stage.fornecedor`) as any, '', { shouldDirty: true, shouldValidate: false });
          }}
        />
        <FormGroup>
          <Label htmlFor={`${stagePath}.quantidade`}>Quantidade</Label>
          <NumericInput
            id={`${itemPath}.stage.quantidade`}
            name={`${itemPath}.stage.quantidade`}
            value={(watch(`${itemPath}.stage.quantidade` as any) as any) || ''}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setValue(`${itemPath}.stage.quantidade` as any, e.target.value, { shouldDirty: true })}
            onBlur={() => {}}
            hasError={!!get(errors, `${itemPath}.stage.quantidade`)}
          />
        </FormGroup>
        <FormGroup>
          <Label>Arquivo</Label>
          <AttachmentInputContainer>
            <AttachmentTrigger onClick={handleFileSelectClick} hasError={!!attachmentError}>
              <Paperclip size={16} />
              <AttachmentPlaceholder>Clique para anexar arquivo</AttachmentPlaceholder>
            </AttachmentTrigger>
            {attachments.length > 0 && (
              <TrashIconContainer
                as="button"
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  handleRemoveAllAttachments();
                }}
              >
                <Trash2 size={16} />
              </TrashIconContainer>
            )}
          </AttachmentInputContainer>
          {attachments.length > 0 && (
            <AttachmentList>
              {attachments.map((file, index) => (
                <AttachmentListItem key={index}>
                  {file.name}
                  <RemoveAttachmentButton
                    type="button"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      handleRemoveAttachment(index);
                    }}
                  >
                    <X size={12} />
                  </RemoveAttachmentButton>
                </AttachmentListItem>
              ))}
            </AttachmentList>
          )}
          <input
            type="file"
            multiple
            ref={fileInputRef}
            onChange={handleFileChange}
            style={{ display: 'none' }}
            accept=".pdf,.xml,.doc,.docx,.xls,.xlsx,.jpeg,.jpg,.png"
          />
          {attachmentError && <FormErrorMessage>{attachmentError}</FormErrorMessage>}
        </FormGroup>
      </FormGrid>
    </>
  );
};

export default ServicesSection;
